/**
 * Pre-built menu themes with excellent accessibility and layout variety
 */
export const themes = [
  {
    id: 'coffeecloud-default',
    name: 'Coffee Default',
    category: 'Default',
    description: 'The classic coffee shop design with professional dark theme',
    colors: {
      primary: '#a15507',
      secondary: '#924e06',
      accent: '#d97706',
      background: 'linear-gradient(to bottom, #1f2937, #111827)',
      surface: '#1f2937',
      text: '#ffffff',
      textSecondary: '#d1d5db',
      border: '#374151',
      success: '#10b981',
      warning: '#f59e0b',
      error: '#ef4444'
    },
    typography: {
      fontFamily: 'Inter, sans-serif',
      headingFont: 'Inter, sans-serif',
      bodyFont: 'Inter, sans-serif',
      baseSize: 16,
      headingSize: 28,
      lineHeight: 1.6,
      fontWeight: '600'
    },
    layout: {
      borderRadius: '12px',
      spacing: '20px',
      shadow: '0 10px 25px rgba(161, 85, 7, 0.3)',
      animation: 'ease-in-out',
      productCardStyle: 'vertical',
      showImages: true,
      showDescriptions: true,
      showCategories: true,
      categoryPosition: 'top',
      gridColumns: 'grid-cols-2',
      layoutStyle: 'grid'
    }
  },
  {
    id: 'coffee-classic',
    name: 'Coffee Classic',
    category: 'Traditional',
    description: 'Warm, traditional coffee shop feel with grid layout',
    colors: {
      primary: '#8B4513',
      secondary: '#A0522D', 
      accent: '#D2691E',
      background: '#FFFEF7',
      surface: '#FFFFFF',
      text: '#2C1810',
      textSecondary: '#5D4037',
      border: '#D7CCC8',
      success: '#4CAF50',
      warning: '#FF9800',
      error: '#F44336'
    },
    typography: {
      fontFamily: 'Playfair Display, serif',
      headingFont: 'Playfair Display, serif',
      bodyFont: 'Lora, serif',
      baseSize: 16,
      headingSize: 28,
      lineHeight: 1.6,
      fontWeight: '600'
    },
    layout: {
      borderRadius: '12px',
      spacing: '20px',
      shadow: '0 4px 12px rgba(139, 69, 19, 0.15)',
      animation: 'ease-in-out',
      productCardStyle: 'vertical',
      showImages: true,
      showDescriptions: true,
      showCategories: true,
      categoryPosition: 'top',
      gridColumns: 'grid-cols-2',
      layoutStyle: 'grid'
    }
  },
  {
    id: 'modern-minimal',
    name: 'Modern Minimal',
    category: 'Contemporary',
    description: 'Clean, minimal design with horizontal list layout',
    colors: {
      primary: '#2C3E50',
      secondary: '#34495E',
      accent: '#3498DB',
      background: '#FAFAFA',
      surface: '#FFFFFF',
      text: '#2C3E50',
      textSecondary: '#7F8C8D',
      border: '#E0E0E0',
      success: '#27AE60',
      warning: '#F39C12',
      error: '#E74C3C'
    },
    typography: {
      fontFamily: 'Inter, sans-serif',
      headingFont: 'Inter, sans-serif',
      bodyFont: 'Inter, sans-serif',
      baseSize: 15,
      headingSize: 22,
      lineHeight: 1.5,
      fontWeight: '600'
    },
    layout: {
      borderRadius: '8px',
      spacing: '16px',
      shadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
      animation: 'ease-out',
      productCardStyle: 'horizontal',
      showImages: true,
      showDescriptions: false,
      showCategories: true,
      categoryPosition: 'top',
      gridColumns: 'space-y-3',
      layoutStyle: 'list'
    }
  },
  {
    id: 'elegant-dark',
    name: 'Elegant Dark',
    category: 'Premium',
    description: 'Luxury dark theme with premium typography',
    colors: {
      primary: '#D4AF37',
      secondary: '#B8860B',
      accent: '#FFD700',
      background: '#1A1A1A',
      surface: '#2D2D2D',
      text: '#F5F5F5',
      textSecondary: '#D4AF37',
      border: '#404040',
      success: '#4CAF50',
      warning: '#FFC107',
      error: '#FF5252'
    },
    typography: {
      fontFamily: 'Playfair Display, serif',
      headingFont: 'Playfair Display, serif',
      bodyFont: 'Lora, serif',
      baseSize: 16,
      headingSize: 30,
      lineHeight: 1.7,
      fontWeight: '400'
    },
    layout: {
      borderRadius: '16px',
      spacing: '24px',
      shadow: '0 8px 32px rgba(212, 175, 55, 0.2)',
      animation: 'ease-in-out',
      productCardStyle: 'vertical',
      showImages: true,
      showDescriptions: true,
      showCategories: true,
      categoryPosition: 'top',
      gridColumns: 'grid-cols-1 md:grid-cols-2',
      layoutStyle: 'grid'
    }
  },
  {
    id: 'vibrant-bold',
    name: 'Vibrant Energy',
    category: 'Energetic',
    description: 'Bold, energetic design with three-column grid',
    colors: {
      primary: '#E91E63',
      secondary: '#AD1457',
      accent: '#FFC107',
      background: '#FFF8E1',
      surface: '#FFFFFF',
      text: '#1A1A1A',
      textSecondary: '#424242',
      border: '#FFE0B2',
      success: '#4CAF50',
      warning: '#FF9800',
      error: '#F44336'
    },
    typography: {
      fontFamily: 'Montserrat, sans-serif',
      headingFont: 'Montserrat, sans-serif',
      bodyFont: 'Open Sans, sans-serif',
      baseSize: 16,
      headingSize: 24,
      lineHeight: 1.6,
      fontWeight: '700'
    },
    layout: {
      borderRadius: '20px',
      spacing: '18px',
      shadow: '0 6px 20px rgba(233, 30, 99, 0.15)',
      animation: 'ease-in-out',
      productCardStyle: 'vertical',
      showImages: true,
      showDescriptions: true,
      showCategories: true,
      categoryPosition: 'top',
      gridColumns: 'grid-cols-2 md:grid-cols-3',
      layoutStyle: 'grid'
    }
  },
  {
    id: 'sheet-style',
    name: 'Coffee Sheet',
    category: 'Professional',
    description: 'Two-column sheet style inspired by traditional menus',
    colors: {
      primary: '#8B4513',
      secondary: '#A0522D',
      accent: '#D2691E',
      background: '#F8F6F0',
      surface: '#FFFFFF',
      text: '#2F1B14',
      textSecondary: '#8B4513',
      border: '#DEB887',
      success: '#228B22',
      warning: '#DAA520',
      error: '#B22222'
    },
    typography: {
      fontFamily: 'Crimson Text, serif',
      headingFont: 'Playfair Display, serif',
      bodyFont: 'Crimson Text, serif',
      baseSize: 16,
      headingSize: 28,
      lineHeight: 1.8,
      fontWeight: '400'
    },
    layout: {
      borderRadius: '0px',
      spacing: '20px',
      shadow: '0 4px 16px rgba(139, 69, 19, 0.1)',
      animation: 'ease-in-out',
      productCardStyle: 'sheet',
      showImages: false,
      showDescriptions: true,
      showCategories: true,
      categoryPosition: 'top',
      gridColumns: 'grid-cols-2',
      layoutStyle: 'sheet'
    }
  },
  {
    id: 'featured-hero',
    name: 'Featured Premium',
    category: 'Premium',
    description: 'Image-heavy template with featured product and grid',
    colors: {
      primary: '#1B4332',
      secondary: '#2D5016',
      accent: '#95D5B2',
      background: '#F1FAEE',
      surface: '#FFFFFF',
      text: '#1B4332',
      textSecondary: '#40916C',
      border: '#95D5B2',
      success: '#52B788',
      warning: '#F77F00',
      error: '#D62828'
    },
    typography: {
      fontFamily: 'Lora, serif',
      headingFont: 'Playfair Display, serif',
      bodyFont: 'Lora, serif',
      baseSize: 16,
      headingSize: 32,
      lineHeight: 1.6,
      fontWeight: '400'
    },
    layout: {
      borderRadius: '12px',
      spacing: '20px',
      shadow: '0 4px 16px rgba(27, 67, 50, 0.1)',
      animation: 'ease-in-out',
      productCardStyle: 'featured',
      showImages: true,
      showDescriptions: true,
      showCategories: true,
      categoryPosition: 'top',
      gridColumns: 'featured-layout',
      layoutStyle: 'featured'
    }
  },
  {
    id: 'sport-energy',
    name: 'Sport Energy',
    category: 'Athletic',
    description: 'Athletic design with compact horizontal layout',
    colors: {
      primary: '#1976D2',
      secondary: '#1565C0',
      accent: '#FFC107',
      background: '#F8F9FA',
      surface: '#FFFFFF',
      text: '#212529',
      textSecondary: '#6C757D',
      border: '#DEE2E6',
      success: '#28A745',
      warning: '#FD7E14',
      error: '#DC3545'
    },
    typography: {
      fontFamily: 'Roboto, sans-serif',
      headingFont: 'Oswald, sans-serif',
      bodyFont: 'Roboto, sans-serif',
      baseSize: 15,
      headingSize: 22,
      lineHeight: 1.5,
      fontWeight: '600'
    },
    layout: {
      borderRadius: '6px',
      spacing: '12px',
      shadow: '0 2px 6px rgba(25, 118, 210, 0.12)',
      animation: 'ease-out',
      productCardStyle: 'horizontal',
      showImages: true,
      showDescriptions: false,
      showCategories: true,
      categoryPosition: 'top',
      gridColumns: 'space-y-2',
      layoutStyle: 'compact'
    }
  }
];

/**
 * Coffee shop slogans for beautiful headers
 */
export const coffeeSlogans = [
  "Brewing happiness, one cup at a time",
  "Where every bean tells a story",
  "Life happens, coffee helps",
  "Perfectly crafted, perfectly yours",
  "Wake up and smell the excellence", 
  "Your daily dose of inspiration",
  "From bean to bliss",
  "Coffee that connects hearts",
  "Fuel your passion, feed your soul",
  "Where moments become memories",
  "Crafted with love, served with care",
  "The perfect blend of taste and tradition",
  "Because great days start with great coffee",
  "Roasted to perfection, served with love",
  "Where coffee meets community"
];

/**
 * Get theme by ID
 * @param {string} id - Theme ID
 * @returns {Object|null} Theme object or null if not found
 */
export const getThemeById = (id) => {
  return themes.find(theme => theme.id === id) || null;
};

/**
 * Get themes by category
 * @param {string} category - Theme category
 * @returns {Array} Array of themes in the category
 */
export const getThemesByCategory = (category) => {
  return themes.filter(theme => theme.category === category);
};

/**
 * Get random coffee slogan
 * @returns {string} Random slogan
 */
export const getRandomSlogan = () => {
  return coffeeSlogans[Math.floor(Math.random() * coffeeSlogans.length)];
};